/**
 * ManagePortfolio — the Manage Accounts portfolio screen, and the app's default
 * section: where a fresh sign-in lands ('/' itself is the Home page).
 *
 * This is the only screen with the dashboard left rail so far, matching the
 * legacy Vue app where the rail belongs to the `/dashboard` layout route rather
 * than to the app shell. The AppBar comes from AppLayout above; the rail comes
 * from SidebarLayout here.
 *
 * ONE page for both products (.claude/rules/variants.md): the Portfolio details
 * + Add instance cards, the instance table, Upload, Save / Cancel. Their values
 * live here because they are what Save will send. Live: the Region and
 * Instance Type lists (hooks/useInstanceOptions) and Add Instance, which has
 * the server check each row (hooks/useAddInstance) and lists it in the table.
 *
 * What differs by product is picked ONCE from PRODUCT_PARTS below, never with
 * scattered ifs:
 *  - EIA: its Add instance card and table, inside the Instance Stats / Self
 *    Perf tabs with the Operational Safety Margin slider; a Self Perf file in
 *    the upload; the safety margin counts as unsaved work.
 *  - CCA (legacy ManageAccount.vue `!application` branch): its own Add
 *    instance card (Quantity + Total Number of Hours per Month) and table,
 *    shown directly — no tabs, slider or Self Perf; CCA templates; the
 *    "Cloud Usage Reports" link; Policy Engine sent with Upload / Save (no
 *    picker — legacy has none: the opened portfolio's saved value, else
 *    "No Policy Engine (Default)").
 *  - CCA Billing (CUR) mode — Service Provider "Billing" (legacy fileType
 *    'curreports'): no Add instance card (and so no Find and Replace / help),
 *    the three CUR report templates under "Templates", requests sent for the
 *    Billing "Cloud" pick, and portfolios opened / uploaded with
 *    is_billing_data. Legacy sent an opened Billing portfolio to the Telemetry
 *    page, so this screen was never reached that way; it opens here.
 *
 * OPENING a saved portfolio: a click in the sidebar list arrives here as a
 * REQUEST (dashboard slice `portfolioRequest`), because only this page knows
 * whether there is unsaved work to warn about first. Opening loads it
 * (GET /get-portfolio with id + name + owner) into the same state a new one
 * uses; Save then updates it, Cancel reloads it.
 *
 * Default-exported for lazy loading.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useHref, useLocation, useNavigate } from 'react-router-dom';

import { AddInstance as CcaAddInstance } from '@components/cca/AddInstance';
import {
  ccaTemplateControlLabel,
  ccaTemplatesFor,
  showsCloudUsageReports,
} from '@components/cca/ccaFormRules';
import {
  applyEdit as applyCcaEdit,
  markRequired as markCcaRequired,
  missingRowFields as missingCcaRowFields,
} from '@components/cca/ccaInstanceRows';
import {
  downloadOtherCloudExcel as downloadCcaOtherCloudExcel,
} from '@components/cca/ccaOtherCloudExport';
import { CloudUsageReportsLink } from '@components/cca/CloudUsageReportsLink';
import { InstanceTable as CcaInstanceTable } from '@components/cca/InstanceTable';
import { ConfirmDialog } from '@components/common/ConfirmDialog';
import {
  contractDiscountProblem,
  emptyContractDiscount,
  sameContractDiscount,
} from '@components/common/ContractDiscountDialog/contractDiscountRules';
import { ChipIcon, ClearIcon, DeleteIcon, SaveIcon } from '@components/common/icons';
import { Note } from '@components/common/Note';
import { AddInstance as EiaAddInstance } from '@components/eia/AddInstance';
import { DataCorrectionHelp } from '@components/eia/DataCorrectionHelp';
import { FindReplaceDialog } from '@components/eia/FindReplaceDialog';
import {
  applyEdit as applyEiaEdit,
  countRemarks,
  hasErrorRemark,
  hasUnsavedRows,
  markRequired as markEiaRequired,
  missingRowFields as missingEiaRowFields,
  toSavedRow,
  withoutErrorRows,
} from '@components/eia/instanceRows';
import { InstanceTable as EiaInstanceTable } from '@components/eia/InstanceTable';
import { INSTANCE_TABS, InstanceTabs } from '@components/eia/InstanceTabs';
import { InstanceUpload } from '@components/eia/InstanceUpload';
import {
  downloadOtherCloudExcel as downloadEiaOtherCloudExcel,
} from '@components/eia/otherCloudExport';
import { PortfolioDetails } from '@components/eia/PortfolioDetails';
import { DEFAULT_HEADROOM, FIELD, validatePortfolioName } from '@components/eia/portfolioFormRules';
import { SafetyMarginSlider } from '@components/eia/SafetyMarginSlider';
import { SelfPerfTable } from '@components/eia/SelfPerfTable';
import { UploadDialog } from '@components/eia/UploadDialog';
import { Button } from '@components/ui';
import { ADVICE_VIEWS, adviceLink, setAdviceView } from '@features/costadvice';
// The dashboard barrel imports its own registerSlice, so pulling from the
// feature's public API also injects its reducer with this chunk.
import {
  BENCHMARK_MODELS,
  billingProvider,
  clearPortfolioRequest,
  getUploadRowLimit,
  isBillingMode,
  portfolioName as nameOf,
  selectOpenPortfolio,
  selectPortfolioRequest,
  selectSelectedModel,
  selectSelectedProvider,
  selectSelectedService,
  selectSelectedTelemetry,
  selectSidebarOpen,
  setSelectedTelemetry,
  setOpenPortfolio,
  setPortfolioScope,
  setSidebarOpen,
  SidebarPanel,
  telemetrySlug,
  toOpenPortfolio,
  useGetPortfoliosQuery,
} from '@features/dashboard';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useNotify } from '@hooks/useNotify';
import { SidebarLayout } from '@layouts/SidebarLayout';
import { setLeaveGuard } from '@router/leaveGuard';
import { selectUser } from '@security/auth/authSlice';
import { FEATURES } from '@security/features';
import { APP_MODES } from '@variant/appMode';
import { useAppMode } from '@variant/useAppMode';
import { VariantGate } from '@variant/VariantGate';

import { readPortfolioDetails, useLazyGetPortfolioDetailsQuery } from '../api/portfolioDetailsApi';
import { readDeleteResult, useDeletePortfolioMutation } from '../api/uploadApi';
import { CloudCredentialView } from '../components/CloudCredentialView';
import { ManagePortfolioSkeleton } from '../components/ManagePortfolioSkeleton';
import { useAddInstance } from '../hooks/useAddInstance';
import { useFindReplace } from '../hooks/useFindReplace';
import { useInstanceOptions } from '../hooks/useInstanceOptions';
import { useSavePortfolio } from '../hooks/useSavePortfolio';
import { useUploadPortfolio } from '../hooks/useUploadPortfolio';

import styles from './ManagePortfolio.module.css';

/**
 * What differs by product, in one place (.claude/rules/variants.md: one page,
 * the variant-specific parts swapped declaratively). Both sets have the same
 * shape and the same component props, so the page below never branches on
 * the product itself — it reads `parts.X`.
 *  - AddInstance / InstanceTable: the product's card and table.
 *  - applyEdit / markRequired / missingRowFields: the table-edit rules.
 *  - downloadOtherCloudExcel: the "other cloud providers" Excel columns.
 *  - instanceTabs: EIA's Instance Stats / Self Perf tabs + safety-margin
 *    slider (it also decides whether the safety margin is unsaved work and is
 *    handed to the advice page).
 *  - selfPerf: a Self Perf Assessment file in the upload, and its rows.
 *  - cloudUsageReports / billing: CCA-only (legacy ManageAccount.vue:51,
 *    fileType 'curreports').
 *  - detailsProps: what Portfolio details needs on top of the shared props —
 *    CCA's templates and their label; EIA keeps the card's defaults.
 */
const PRODUCT_PARTS = Object.freeze({
  [APP_MODES.CCA]: Object.freeze({
    AddInstance: CcaAddInstance,
    InstanceTable: CcaInstanceTable,
    applyEdit: applyCcaEdit,
    markRequired: markCcaRequired,
    missingRowFields: missingCcaRowFields,
    downloadOtherCloudExcel: downloadCcaOtherCloudExcel,
    instanceTabs: false,
    selfPerf: false,
    cloudUsageReports: true,
    billing: true,
    detailsProps: ({ provider, billing }) => ({
      templates: ccaTemplatesFor(provider, { billing }),
      templateLabel: ccaTemplateControlLabel({ billing }),
    }),
  }),
  [APP_MODES.EIA]: Object.freeze({
    AddInstance: EiaAddInstance,
    InstanceTable: EiaInstanceTable,
    applyEdit: applyEiaEdit,
    markRequired: markEiaRequired,
    missingRowFields: missingEiaRowFields,
    downloadOtherCloudExcel: downloadEiaOtherCloudExcel,
    instanceTabs: true,
    selfPerf: true,
    cloudUsageReports: false,
    billing: false,
    detailsProps: () => ({}),
  }),
});

/**
 * The Add instance card with its live option lists. A component of its own so
 * the Region / Instance Type requests only happen where the card is shown —
 * never in CCA's Billing mode, which has no card.
 *
 * `card` is the product's AddInstance (PRODUCT_PARTS); both take these props.
 *
 * Keyed by Service Provider AND cloud at the call site: any change to either
 * remounts the card with an empty form — legacy clears the form on every
 * Service Provider / Cloud pick (Cloud.vue updateCloud, clearTelemetryInputFields),
 * and the old region and instance type may not exist on the new cloud anyway.
 */
function AddInstanceSection({ card: AddInstance, provider, onAdd, disabled, ...actions }) {
  const [region, setRegion] = useState('');
  const options = useInstanceOptions({ provider, region });

  return (
    <AddInstance
      provider={provider}
      onRegionChange={setRegion}
      onAdd={onAdd}
      disabled={disabled}
      {...actions}
      {...options}
    />
  );
}

/**
 * Find and Replace with its "To" lists: the provider's regions and EVERY
 * instance type it has (/cloud-instances). Mounted only while open, so the
 * lists are asked for on each opening — as legacy does — and never otherwise.
 */
function FindReplaceSection({ provider, ...dialogProps }) {
  const options = useInstanceOptions({
    provider,
    region: '',
    instanceTypes: false,
    allInstanceTypes: true,
  });

  return (
    <FindReplaceDialog
      regions={{
        list: options.regions,
        loading: options.regionsLoading,
        error: options.regionsError,
      }}
      instanceTypes={{
        list: options.allInstanceTypes,
        loading: options.allInstanceTypesLoading,
        error: options.allInstanceTypesError,
      }}
      {...dialogProps}
    />
  );
}

/**
 * The instance table with the option lists its cell editor needs. Lists load
 * only while a Region or Instance Type cell is open — regions for the
 * provider, instance types for THAT row's region (legacy editField, :948-952).
 * `table` is the product's InstanceTable (PRODUCT_PARTS); both take these props.
 */
function InstanceTableSection({
  table: InstanceTable,
  provider,
  showRegionsLink = true,
  ...tableProps
}) {
  const [editingCell, setEditingCell] = useState(null);
  const field = editingCell?.field;
  // The regions page, as a real address (with the /cca|/eia prefix locally) so
  // it can open in a new tab.
  const regionsHref = useHref(`/regions-list?providerName=${encodeURIComponent(provider)}`);

  const options = useInstanceOptions({
    provider,
    region: editingCell?.row?.[FIELD.REGION] ?? '',
    regions: field === FIELD.REGION,
    instanceTypes: field === FIELD.INSTANCE_TYPE,
  });

  return (
    <InstanceTable
      provider={provider}
      onEditStart={setEditingCell}
      regionOptions={{ list: options.regions, loading: options.regionsLoading }}
      instanceTypeOptions={{ list: options.instanceTypes, loading: options.instanceTypesLoading }}
      regionsHref={provider && showRegionsLink ? regionsHref : undefined}
      {...tableProps}
    />
  );
}

export default function ManagePortfolio() {
  const dispatch = useDispatch();
  const { mode } = useAppMode();
  // The product's parts (CCA or EIA); unknown modes fall back to EIA's.
  const parts = PRODUCT_PARTS[mode] ?? PRODUCT_PARTS[APP_MODES.EIA];
  const selectedModel = useSelector(selectSelectedModel);
  const provider = useSelector(selectSelectedProvider);
  // What the Service Provider picker shows (a cloud or a connector).
  const service = useSelector(selectSelectedService);
  // The picked connector ('' for a cloud).
  const telemetry = useSelector(selectSelectedTelemetry);
  /**
   * CCA Billing (CUR reports) mode: Service Provider "Billing" picked (legacy
   * fileType 'curreports'). Never on EIA, which has no Billing connector.
   */
  const billing = parts.billing && isBillingMode(telemetry);
  /**
   * The cloud Upload / Save are sent for: in Billing mode the Billing "Cloud"
   * pick (legacy datadogCSP), never the word "Billing"; otherwise the provider.
   */
  const requestProvider = billingProvider({ telemetry, provider });
  // Shared state (legacy Vuex `toggle`), so the rail stays closed or open as
  // the user left it when they come back to this page.
  const sidebarOpen = useSelector(selectSidebarOpen);

  const [portfolioName, setPortfolioName] = useState('');
  /**
   * The contract discount on screen, { onDemand, oneYearRi, threeYearRi }
   * (the dialog's strings; '' = blank). Upload and Save send it as
   * pricing_model_discount (converted in the API files, nowhere else).
   */
  const [contractDiscount, setContractDiscount] = useState(emptyContractDiscount);
  const [instances, setInstances] = useState([]);
  /** Rows of the uploaded Self Perf Assessment file (the second tab). */
  const [selfPerfRows, setSelfPerfRows] = useState([]);
  /** The upload dialog: open with the picked instances file, or closed (null). */
  const [uploadDialog, setUploadDialog] = useState(null);
  /**
   * The portfolio the last upload created on the server. Kept for Save /
   * Cancel (legacy uploadedPortfolioID + isNewUploadedPortfolio): Cancel must
   * delete it if it is never saved; Save updates it instead of creating a new
   * one. A ref: it never changes what is on screen.
   */
  const uploadedPortfolioId = useRef(null);

  const notify = useNotify();
  // Legacy sends model 'Scaler' only for the General IT Average costing path.
  const model = selectedModel === BENCHMARK_MODELS.GENERAL_IT_AVERAGE ? 'Scaler' : undefined;
  const { addInstance, validating, revalidateInstance, revalidating } = useAddInstance({
    mode,
    provider,
    model,
  });
  /**
   * Operational Safety Margin (legacy `headroom`), set by the slider beside the
   * tabs (EIA). Moving it calls no API: it is only kept here and sent with
   * Upload and Save (legacy: store headroomVal, default 20). CCA has no slider,
   * and its requests leave headroom out (api/uploadApi, api/saveApi).
   */
  const [headroom, setHeadroom] = useState(DEFAULT_HEADROOM);
  /**
   * CCA Policy Engine: the opened portfolio's saved value (legacy
   * UploadDownloadTemplate.vue :236-244), null for a new one. There is no
   * picker (legacy has none), so Upload / Save send this or, when null,
   * "No Policy Engine (Default)" (ccaFormRules policyEngineFor). EIA ignores it.
   */
  const [savedPolicyEngine, setSavedPolicyEngine] = useState(null);
  const {
    upload,
    stage: uploadStage,
    uploading,
  } = useUploadPortfolio({
    mode,
    provider: requestProvider,
    model,
    headroom,
    discount: contractDiscount,
    policyEngine: savedPolicyEngine,
    billing,
  });
  const { save, saving } = useSavePortfolio({
    mode,
    provider: requestProvider,
    model,
    headroom,
    discount: contractDiscount,
    policyEngine: savedPolicyEngine,
  });
  const [deletePortfolio] = useDeletePortfolioMutation();
  const [fetchPortfolioDetails] = useLazyGetPortfolioDetailsQuery();

  // This user's portfolios — Save refuses a name already taken. Same cached
  // request as the sidebar list, so nothing extra is fetched.
  const email = useSelector(selectUser)?.email ?? '';
  const { data: portfolioLists, refetch: refetchPortfolios } = useGetPortfoliosQuery(email, {
    skip: !email,
  });

  /** The saved portfolio open here (sidebar highlight), or null for a new one. */
  const openedPortfolio = useSelector(selectOpenPortfolio);
  const portfolioRequest = useSelector(selectPortfolioRequest);
  /** True while a saved portfolio is being fetched into the page. */
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  /** Only the latest load may fill the page (click A, then B: A must not win). */
  const loadSeq = useRef(0);
  /**
   * A SAVED row was deleted. That is unsaved work too (legacy
   * hasUnsavedChanges also counts removedInstances that were saved), though
   * every row left on screen is still marked saved.
   */
  const [removedSaved, setRemovedSaved] = useState(false);
  /**
   * The contract discount the opened portfolio was loaded with. A different
   * value on screen is unsaved work — legacy ignored it (hasUnsavedChanges
   * looks at rows only), so a changed discount could be lost without warning,
   * and Instance Advice ran on a value that was never saved.
   */
  const [savedDiscount, setSavedDiscount] = useState(emptyContractDiscount);
  /** The safety margin the opened portfolio was loaded with — same idea. */
  const [savedHeadroom, setSavedHeadroom] = useState(DEFAULT_HEADROOM);

  /** Bumped to give the Add Instance card a fresh, empty form. */
  const [formVersion, setFormVersion] = useState(0);
  /**
   * "Discard unsaved changes?" — null when closed, else { next, busy }. `next`
   * is what Yes goes on to do: { kind: 'cancel' } (the Cancel button), or a
   * sidebar request ({ kind: 'open', portfolio } / { kind: 'new' }).
   */
  const [discardDialog, setDiscardDialog] = useState(null);
  /** "Confirm Delete Portfolio": closed (null), open, or running ('busy'). */
  const [deleteDialog, setDeleteDialog] = useState(null);
  /**
   * "Instances from Other Cloud Providers Detected": the upload's rows for
   * another cloud, offered as an Excel download — null when closed, else
   * { rows, busy }.
   */
  const [otherCloud, setOtherCloud] = useState(null);
  /** "How data corrections are applied" (the ⓘ) is open. */
  const [helpOpen, setHelpOpen] = useState(false);
  /**
   * What kind of portfolio is open: 'manage' (the cards and table) or
   * 'cloud-credential' — a 🔑 portfolio synced from a cloud account, shown
   * read-only in place of the cards (legacy's List of Instances page).
   */
  const [openKind, setOpenKind] = useState('manage');
  const cloudView = openKind === 'cloud-credential' && Boolean(openedPortfolio);
  /** The Find and Replace dialog is open. */
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const { replaceAll, replacing } = useFindReplace({ mode, provider, model });
  /** A table cell is being edited or re-checked (legacy `isFieldUpdated`). */
  const [cellEditing, setCellEditing] = useState(false);

  /**
   * Back to a blank page — after a confirmed Cancel, or the ✕ (legacy clears
   * the instances, the temp lists and the input fields).
   */
  const resetPage = () => {
    loadSeq.current += 1; // a load still in flight must not refill the page
    setInstances([]);
    setSelfPerfRows([]);
    setPortfolioName('');
    setContractDiscount(emptyContractDiscount());
    setSavedDiscount(emptyContractDiscount());
    setHeadroom(DEFAULT_HEADROOM);
    setSavedHeadroom(DEFAULT_HEADROOM);
    setSavedPolicyEngine(null);
    setRemovedSaved(false);
    setFindReplaceOpen(false);
    uploadedPortfolioId.current = null;
    setFormVersion((v) => v + 1);
  };

  /**
   * Fill the page from a saved portfolio (legacy getPortfolioInstances): its
   * instances and self-perf rows (EIA), marked saved; its name, contract
   * discount, safety margin (EIA) and policy engine (CCA). A portfolio opened
   * in Billing mode is asked for with is_billing_data (legacy Portfolios.vue
   * :465) — its own `billing` flag, so Cancel and the reload after Save ask
   * the same way. False if it could not be loaded (a toast says why).
   */
  const loadPortfolio = async (portfolio) => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLoadingPortfolio(true);
    try {
      const details = readPortfolioDetails(
        await fetchPortfolioDetails({
          id: portfolio.id,
          name: portfolio.name,
          user: portfolio.user,
          billing: Boolean(portfolio.billing),
        }).unwrap(),
      );
      if (seq !== loadSeq.current) return false;
      setInstances(details.instances.map(toSavedRow));
      setSelfPerfRows(parts.selfPerf ? details.selfPerf.map(toSavedRow) : []);
      setSavedPolicyEngine(details.policyEngine);
      setPortfolioName(portfolio.name);
      // None saved (null) -> all three blank.
      const discount = details.discount ?? emptyContractDiscount();
      setContractDiscount(discount);
      setSavedDiscount(discount);
      setHeadroom(details.headroom);
      setSavedHeadroom(details.headroom);
      setRemovedSaved(false);
      uploadedPortfolioId.current = null;
      setFormVersion((v) => v + 1);
      return true;
    } catch (error) {
      if (seq === loadSeq.current) notify.error(error, 'Could not load the portfolio.');
      return false;
    } finally {
      if (seq === loadSeq.current) setLoadingPortfolio(false);
    }
  };

  /**
   * Open a saved portfolio. The sidebar takes its Model / Service Provider
   * first (slice setOpenPortfolio), which empties the page for the new
   * provider; then the load fills it. If the load fails, nothing is left
   * open — a highlighted portfolio with an empty page would invite saving
   * nothing over it.
   */
  const openSavedPortfolio = async (portfolio) => {
    dispatch(setOpenPortfolio(portfolio));
    resetPage();
    const loaded = await loadPortfolio(portfolio);
    if (!loaded) dispatch(setOpenPortfolio(null));
  };

  /** A portfolio an upload created but nobody saved: remove it (legacy :506-518). */
  const dropUploadedPortfolio = async () => {
    const id = uploadedPortfolioId.current;
    uploadedPortfolioId.current = null;
    // Belt and braces: a saved, opened portfolio is never the page's to delete.
    if (!id || id === openedPortfolio?.id) return;
    try {
      await deletePortfolio(id).unwrap();
    } catch {
      // Silent, as legacy — the discard itself still happens.
    }
  };

  /** Carry out a sidebar request, or the Cancel button, with nothing unsaved left. */
  const carryOut = async (next) => {
    await dropUploadedPortfolio();
    if (next.kind === 'open' && next.target === 'telemetry') {
      // A telemetry portfolio (e.g. from a notification) is shown on the
      // Telemetry page: pick its connector and hand the portfolio over there.
      // Navigating here, not via the connector redirect below, so the
      // portfolio rides along in the navigation state.
      const slug = telemetrySlug(next.connector);
      if (!slug) return;
      leaving.current = true;
      dispatch(setSelectedTelemetry(next.connector));
      navigate(`/telemetry?type=${slug}`, {
        replace: true,
        state: { openTelemetryPortfolio: next.portfolio },
      });
    } else if (next.kind === 'open' && next.target === 'cost-advice') {
      // A finished large-data portfolio has no instances page: it opens on its
      // advice, on Analytics (legacy changePortfolio: updateViewMode(0), then
      // push /costadvice). Its own saved values apply — nothing is handed over.
      leaving.current = true;
      dispatch(setAdviceView(ADVICE_VIEWS.ANALYTICS));
      navigate(...adviceLink(next.portfolio.id));
    } else if (next.kind === 'open' && next.target === 'cloud-credential') {
      // Read-only, loaded by its own view: nothing goes into this page's lists.
      dispatch(setOpenPortfolio(next.portfolio));
      resetPage();
      setOpenKind('cloud-credential');
    } else if (next.kind === 'open') {
      setOpenKind('manage');
      await openSavedPortfolio(next.portfolio);
    } else if (next.kind === 'new') {
      dispatch(setOpenPortfolio(null));
      resetPage();
    } else if (next.kind === 'scope') {
      // My / Org switch (legacy clearPortfoliosData): the new scope, nothing open.
      dispatch(setPortfolioScope(next.scope));
      dispatch(setOpenPortfolio(null));
      resetPage();
    } else if (next.kind === 'service') {
      // The approved Service Provider / Cloud pick; the reset below (keyed on
      // the service) then empties the list for the new cloud.
      dispatch(next.change);
    } else if (next.kind === 'cloud-usage') {
      // CCA's "Cloud Usage Reports" link (legacy cloudUsageReportsRedirect,
      // ManageAccount.vue:558-574): nothing left open, the page emptied, then
      // the Cloud Usage Reports page.
      leaving.current = true;
      dispatch(setOpenPortfolio(null));
      resetPage();
      navigate('/cloud-usage-reports');
    } else if (openedPortfolio) {
      // Cancel on an opened portfolio: back to what is saved (legacy :521-523).
      resetPage();
      await loadPortfolio(openedPortfolio);
    } else {
      resetPage();
    }
  };

  // A new Service Provider / Cloud empties the instance list, as legacy's
  // updateCloud does (clearInstances): rows checked against one cloud do not
  // belong to a portfolio on another. Adjusted during render, not in an effect,
  // so the old rows never paint under the new provider.
  // The sidebar asks first (a { kind: 'service' } request, unsaved prompt), so
  // by the time the service changes here the user has agreed to lose the list.
  const serviceKey = `${service}|${provider}`;
  const [instancesFor, setInstancesFor] = useState(serviceKey);
  if (instancesFor !== serviceKey) {
    setInstancesFor(serviceKey);
    setInstances([]);
    setSelfPerfRows([]);
    // And the safety margin starts again at 20 (legacy resetHeadRoomValue).
    setHeadroom(DEFAULT_HEADROOM);
    setRemovedSaved(false);
    setFindReplaceOpen(false);
  }

  // A NEW portfolio's discount alone is not unsaved work: Save needs instances,
  // and those already count.
  const discountChanged =
    Boolean(openedPortfolio) && !sameContractDiscount(contractDiscount, savedDiscount);
  /*
   * The safety margin too, on an opened portfolio. Legacy ignored it, so a
   * moved slider was lost without a word on the next portfolio click, and
   * Cancel stayed off. Save sends it, so it is the portfolio's data like the
   * discount; Instance Advice waits for it to be saved, as for the discount.
   * EIA only: CCA has no slider and never sends a safety margin.
   */
  const headroomChanged =
    parts.instanceTabs && Boolean(openedPortfolio) && headroom !== savedHeadroom;
  const unsaved = hasUnsavedRows(instances) || removedSaved || discountChanged || headroomChanged;

  /*
   * A sidebar request (open a portfolio / ✕ new one). Taken off the slice at
   * once so it is handled exactly one time; with unsaved work it waits behind
   * "discard changes?", otherwise it goes ahead. Yes then carries out THIS
   * request — legacy's Yes only discarded, and the click had to be repeated.
   * The ref keeps the effect on the request alone, with today's handlers.
   */
  const onPortfolioRequest = useRef(null);
  onPortfolioRequest.current = (request) => {
    // Its sidebar row is the portfolio this page is building (an upload created
    // it; not saved yet). Opening it would first discard it — deleting it on
    // the server — and then fail to load it. It is already on screen.
    if (
      request.kind === 'open' &&
      request.portfolio?.id &&
      request.portfolio.id === uploadedPortfolioId.current
    ) {
      notify.info('This is the portfolio you are working on. Save it to keep your changes.');
      return;
    }
    if (unsaved) setDiscardDialog({ next: request, busy: false });
    else carryOut(request);
  };
  useEffect(() => {
    if (!portfolioRequest) return;
    dispatch(clearPortfolioRequest());
    onPortfolioRequest.current(portfolioRequest);
  }, [portfolioRequest, dispatch]);

  // Leaving the page closes the portfolio, so the sidebar is not left locked
  // to one that is no longer on screen (legacy: route guard clears it).
  useEffect(() => () => dispatch(setOpenPortfolio(null)), [dispatch]);

  /*
   * LEAVING with unsaved work asks first — the top bar's links, the browser's
   * Back / Forward (useBlocker), and Sign out (router/leaveGuard; the router
   * cannot block that one). Legacy asked on its menu and Logout (Headerbar.vue
   * redirect :552-560), not on Back. Yes discards and goes; No stays. Legacy's
   * Yes only discarded, and the user had to click the link again.
   *
   * Only a change of page is blocked — a notification opening a portfolio stays
   * on this page and goes through the request path above, with its own prompt.
   * `leaving` lets the approved navigation (and sign-out's own) through.
   */
  const leaving = useRef(false);
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
      unsaved && !leaving.current && currentLocation.pathname !== nextLocation.pathname,
    [unsaved],
  );
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state === 'blocked') setDiscardDialog({ next: { kind: 'leave' }, busy: false });
  }, [blocker.state]);

  /*
   * A telemetry CONNECTOR picked in the sidebar (Datadog, CloudWatch, …) has
   * its own page — legacy Cloud.vue pushes /dashboard/telemetry?type=<slug>.
   * Go there; by now any unsaved work has been asked about (the pick came
   * through the 'service' request). Billing has no slug: it stays here.
   * `replace`, so Back does not land on this page only to bounce again. The
   * navigation state rides along, so a notification's "open this portfolio"
   * is not lost on the way.
   */
  const navigate = useNavigate();
  const location = useLocation();
  const telemetryPage = telemetrySlug(telemetry);
  useEffect(() => {
    // Already on the way (a telemetry portfolio being handed over): stay out.
    if (!telemetryPage || leaving.current) return;
    leaving.current = true;
    navigate(`/telemetry?type=${telemetryPage}`, { replace: true, state: location.state });
    // Only a newly picked connector moves the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telemetryPage]);

  /** Sign-out's question; one prompt even if Sign out is clicked twice. */
  const pendingLeave = useRef(null);
  useEffect(() => {
    if (!unsaved) return undefined;
    return setLeaveGuard(() => {
      if (!pendingLeave.current) {
        pendingLeave.current = new Promise((resolve) => {
          const settle = (ok) => {
            pendingLeave.current = null;
            resolve(ok);
          };
          setDiscardDialog({ next: { kind: 'leave', resolve: settle }, busy: false });
        });
      }
      return pendingLeave.current;
    });
  }, [unsaved]);

  /*
   * A portfolio an upload created and nobody saved, left behind with nothing
   * unsaved to ask about (every uploaded row deleted): remove it on the way
   * out, so it does not appear in the sidebar as if saved. Read at unmount.
   */
  const dropOnLeave = useRef(null);
  dropOnLeave.current = () => {
    const id = uploadedPortfolioId.current;
    if (id && id !== openedPortfolio?.id) deletePortfolio(id);
  };
  useEffect(() => () => dropOnLeave.current?.(), []);

  /**
   * An instances file was picked (either Upload File button) or dropped: confirm
   * it. EIA's empty card also takes the Self Perf Assessment file dropped with
   * it (the second file), which arrives as `selfPerfFile`.
   */
  const onPickInstancesFile = (file, selfPerfFile = null) =>
    setUploadDialog({ file, selfPerfFile });

  /**
   * Upload confirmed. The rows join the table (after any added by hand) and
   * the self-perf rows fill the second tab; a large file is processed in the
   * background instead (sidebar progress). A failure keeps the dialog open so
   * the user can retry or cancel — the flow has already told them why.
   *
   * With a saved portfolio open, the file goes INTO it. That is only offered
   * while it has no instances — Portfolio details shows its Upload File button
   * only then, as legacy disables its file input for an opened portfolio with
   * instances.
   */
  const onUpload = async ({ instancesFile, selfPerfFile }) => {
    const result = await upload({
      instancesFile,
      selfPerfFile,
      portfolioName,
      // An opened portfolio takes the file into itself (legacy portfolio_id).
      portfolioId: openedPortfolio?.id,
    });
    if (!result) return;
    setUploadDialog(null);

    if (result.kind === 'background') {
      // A large file is processed on the server and can't be shown here, so
      // the page starts afresh — as legacy's clearMPInputFields does. Its id is
      // NOT kept: a kept id made the next ✕ / sidebar click DELETE a portfolio
      // the server was still working on. An opened portfolio is closed too:
      // it is now a large-data one, and Save would overwrite it with nothing.
      if (openedPortfolio) dispatch(setOpenPortfolio(null));
      resetPage();
      return;
    }

    // Only a portfolio this upload CREATED is the page's to discard on Cancel
    // (legacy isNewUploadedPortfolio); an opened one is updated by Save.
    if (result.created) uploadedPortfolioId.current = result.portfolioId;
    setInstances((rows) => [...rows, ...result.rows]);
    // Legacy keeps the existing self-perf rows when the upload has none. CCA
    // has no Self Perf at all.
    if (parts.selfPerf && result.selfPerfRows.length > 0) setSelfPerfRows(result.selfPerfRows);
    // Opens by itself, as legacy's (Instances.vue fileUploadedData watcher).
    if (result.otherCloudRows.length > 0) {
      setOtherCloud({ rows: result.otherCloudRows, busy: false });
    }
  };

  /**
   * Download Excel. Legacy only logged a failure to the console and the user
   * saw nothing; here a toast says so and the dialog stays, so they can retry.
   * The columns are the product's (legacy Instances.vue downloadExcel).
   */
  const onDownloadOtherCloud = async () => {
    setOtherCloud((d) => ({ ...d, busy: true }));
    try {
      await parts.downloadOtherCloudExcel(otherCloud.rows);
      setOtherCloud(null);
    } catch (error) {
      setOtherCloud((d) => ({ ...d, busy: false }));
      notify.error(error, 'Could not create the Excel file. Please try again.');
    }
  };

  /** Add Instance: server check first; false keeps the form for a retry. */
  /*
   * Which page an Add / edit answer belongs to: the service, the open
   * portfolio and the form generation (bumped by every reset and load). An
   * answer that arrives after any of them changed is for a page that is gone —
   * it is dropped, never added to whatever is on screen now.
   */
  const pageContext = useRef('');
  pageContext.current = `${serviceKey}|${openedPortfolio?.id ?? ''}|${formVersion}`;

  /**
   * Add Instance: server check first; false keeps the form for a retry. The new
   * row goes to the TOP of the list, as legacy (store ADD_INSTANCE unshift) —
   * at the end it landed on the last page and seemed to vanish.
   */
  const onAddInstance = async (row) => {
    const before = pageContext.current;
    const tableRow = await addInstance(row);
    if (!tableRow || pageContext.current !== before) return false;
    setInstances((rows) => [tableRow, ...rows]);
    return true;
  };

  /** Put a changed row back in its place in the list. */
  const replaceRow = (next) =>
    setInstances((rows) => rows.map((r) => (r._rowId === next._rowId ? next : r)));

  /**
   * A cell edited in the table (legacy saveField / saveEdit):
   *  - a required cell left empty -> keep the edit, flag it "Required", no
   *    server call (the server would refuse the row and the edit be lost);
   *  - otherwise -> the server re-checks the row; on success the row takes the
   *    server's version and remarks, on failure it stays as it was (the old
   *    value comes back) and a toast says why.
   * The rules are the product's (PRODUCT_PARTS). On CCA, emptying Quantity or
   * Hours is no change at all — the same row comes back and nothing is sent
   * (legacy handleQuantityChange / handlehourschange put the old value back).
   */
  const onEditCell = async (row, field, value) => {
    const edited = parts.applyEdit(row, field, value, provider);
    if (edited === row) return;
    if (parts.missingRowFields(edited, provider).length > 0) {
      replaceRow(parts.markRequired(edited, provider));
      // Kept, but not checked or saved yet — say so, or it reads as done.
      notify.info('Change kept. Fill the fields marked Required to update the instance.');
      return;
    }
    const before = pageContext.current;
    const next = await revalidateInstance(edited);
    // Moved to another portfolio / service meanwhile: not this page's row.
    if (next && pageContext.current === before) replaceRow(next);
  };

  /** Delete (n) in the table — local only until Save, as in legacy. */
  const onDeleteInstances = (rowIds) => {
    const gone = new Set(rowIds);
    if (instances.some((r) => gone.has(r._rowId) && r.isSaved)) setRemovedSaved(true);
    setInstances((rows) => rows.filter((r) => !gone.has(r._rowId)));
    // Legacy's wording (InstancesListTable.vue:816).
    notify.success(`Instances (${rowIds.length}) deleted successfully`);
  };

  /* --- Save / Cancel (legacy FooterActions.vue) --------------------------- */

  const { errors } = countRemarks(instances);
  const busy =
    saving ||
    uploading ||
    validating ||
    revalidating ||
    loadingPortfolio ||
    replacing ||
    deleteDialog === 'busy';

  /*
   * Find and Replace works on what is in the table, so it needs rows, and
   * nothing else changing them meanwhile. Legacy only disabled it while the
   * page loaded; with an empty table its From lists would just be empty.
   */
  const findReplaceBlockedBy =
    instances.length === 0
      ? 'Add or upload instances first.'
      : busy || cellEditing
        ? 'Wait for the current change to finish.'
        : null;

  /**
   * Delete Error Records (legacy BulkDelete.vue :166-210): drop every row with
   * an error remark; warning-only rows stay. Local until Save, like Delete (n).
   *
   * If nothing is left of a portfolio an UPLOAD just created, that portfolio is
   * deleted on the server too and the page starts afresh, as legacy does —
   * but the "deleted" toast only shows when the delete really worked (legacy
   * showed it even after a failure). An opened, saved portfolio is never
   * deleted here: its emptied list is unsaved work, for Save or Cancel.
   */
  const onDeleteErrorRows = async () => {
    const { kept, removed } = withoutErrorRows(instances);
    if (removed === 0) return;
    if (instances.some((r) => r.isSaved && hasErrorRemark(r))) setRemovedSaved(true);

    const uploadedId = uploadedPortfolioId.current;
    if (kept.length === 0 && uploadedId && uploadedId !== openedPortfolio?.id) {
      try {
        readDeleteResult(await deletePortfolio(uploadedId).unwrap());
        uploadedPortfolioId.current = null;
        resetPage();
        notify.warning('No valid records remain. Portfolio has been deleted.');
      } catch (error) {
        // The rows still go; the id is kept so Cancel can try the delete again.
        setInstances(kept);
        notify.error(error, 'No valid records remain, but the portfolio could not be deleted.');
      }
      return;
    }

    setInstances(kept);
    notify.success('Error records deleted successfully');
  };

  /** Replace all: the server's corrected rows replace the table (legacy :324). */
  const onReplaceAll = async (pairs) => {
    const next = await replaceAll(instances, pairs);
    if (!next) return; // failed — the toast said why; dialog and table unchanged
    setInstances(next);
    setFindReplaceOpen(false);
  };
  // Legacy's Save is disabled with no instances, any error remark, while busy,
  // or with an invalid contract discount (FooterActions.vue :35).
  const saveBlockedBy =
    instances.length === 0
      ? 'Add or upload at least one instance first.'
      : errors > 0
        ? `Fix the ${errors} error${errors === 1 ? '' : 's'} in the instance table first.`
        : contractDiscountProblem(contractDiscount);

  /*
   * Instance Advice (CCA: Cost Advice) — legacy FooterActions.vue :41-46.
   * Enabled only for a SAVED, opened portfolio that is ready to advise on:
   * advice is computed from what is stored on the server, so nothing may be
   * pending on screen. Error remarks do NOT block it (legacy: only Save).
   * The click opens the advice page for this portfolio on Analytics, with the
   * contract discount and safety margin on screen handed over (legacy
   * RedirectCostAdvice: updateViewMode(0), then router push). CCA has no
   * safety margin, so only the discount goes with it there.
   */
  const adviceBlockedBy = !openedPortfolio
    ? 'Open a saved portfolio first.'
    : instances.length === 0
      ? 'The portfolio has no instances.'
      : unsaved
        ? 'Save or cancel your changes first.'
        : cellEditing
          ? 'Finish editing the instance table first.'
          : contractDiscountProblem(contractDiscount);

  const onAdvice = () => {
    if (!openedPortfolio?.id) return;
    dispatch(setAdviceView(ADVICE_VIEWS.ANALYTICS));
    navigate(
      ...adviceLink(openedPortfolio.id, {
        discount: contractDiscount,
        ...(parts.instanceTabs ? { headroom } : {}),
      }),
    );
  };

  /**
   * Save, then show what was saved, as legacy does: an opened portfolio is
   * loaded again (its rows come back marked saved); a NEW one is found in the
   * refreshed list and opened, so its next Save is an update, not a duplicate.
   */
  const onSave = async () => {
    const savedName = await save({
      name: portfolioName,
      rows: instances,
      // Empty on CCA (no Self Perf); its body leaves `udf` out anyway.
      selfPerfRows,
      // Already on the server — opened, or created by an upload: update it.
      portfolioId: openedPortfolio?.id ?? uploadedPortfolioId.current,
      owner: openedPortfolio?.user,
      existingPortfolios: [...(portfolioLists?.own ?? []), ...(portfolioLists?.team ?? [])],
    });
    if (!savedName) return;

    if (openedPortfolio) {
      const renamed = { ...openedPortfolio, name: savedName };
      dispatch(setOpenPortfolio(renamed));
      await loadPortfolio(renamed);
      return;
    }
    const { data: lists } = await refetchPortfolios();
    const created = (lists?.own ?? []).find((p) => nameOf(p) === savedName && p?.user === email);
    // Saved in Billing mode: it stays a Billing portfolio once opened.
    if (created) await openSavedPortfolio(toOpenPortfolio(created, { billing }));
    else resetPage(); // saved, but not listed yet — start afresh; it is in the sidebar soon
  };

  /**
   * Delete Portfolio confirmed (legacy FooterActions.vue DeletePortfolio):
   * DELETE /delete-portfolio?id=<open portfolio>, the server's message as a
   * toast, then a blank page with nothing open; the sidebar list refreshes
   * through the 'Portfolio' tag. Two legacy bugs fixed: a FAILED delete
   * (HTTP error, or ErrorCode -1) no longer shows green and clears the page —
   * it shows the error and leaves the portfolio open.
   */
  const onConfirmDelete = async () => {
    if (!openedPortfolio?.id) return;
    setDeleteDialog('busy');
    try {
      const message = readDeleteResult(await deletePortfolio(openedPortfolio.id).unwrap());
      setDeleteDialog(null);
      dispatch(setOpenPortfolio(null));
      resetPage();
      notify.success(message);
    } catch (error) {
      setDeleteDialog(null);
      notify.error(error, 'Error while deleting portfolio. Please try again.');
    }
  };

  /** "Discard changes?" answered No (or closed): stay, and stay on this page. */
  const onCancelDiscard = () => {
    const next = discardDialog?.next;
    if (next?.kind === 'leave') {
      if (next.resolve) next.resolve(false);
      else if (blocker.state === 'blocked') blocker.reset();
    }
    setDiscardDialog(null);
  };

  /** "Discard changes?" answered Yes. */
  const onConfirmDiscard = async () => {
    const { next } = discardDialog;
    setDiscardDialog({ next, busy: true });
    if (next.kind === 'leave') {
      // Going to another page or signing out: drop what only this page made
      // (an unsaved upload) while the session is still valid — not while a
      // Save is updating it — then let the navigation / sign-out go ahead.
      // Nothing is reset or reloaded: the page is about to go away.
      leaving.current = true;
      if (!saving) await dropUploadedPortfolio();
      setDiscardDialog(null);
      if (next.resolve) next.resolve(true);
      else if (blocker.state === 'blocked') blocker.proceed();
      return;
    }
    await carryOut(next);
    setDiscardDialog(null);
    if (next.kind === 'cancel') notify.info('Changes discarded.');
    // Back to the top of the page, as legacy cancelInstances does on every
    // confirmed discard — the window is what scrolls on this page. A jump, not
    // a glide, for anyone who asked for less motion.
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo?.({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  };

  /*
   * What this user may do here — the server's feature switches (FeaturesData),
   * as legacy's v-permission checks them (security/features.js). Not allowed =
   * not shown, as legacy removes the element. Legacy keys per control:
   *  - Portfolio details: file-upload/validate OR input/validate
   *    (ManageAccount.vue:8,23); its Template menu: file-upload/validate;
   *  - Add instance + editing table cells: input/validate;
   *  - Upload: file-upload/validate;
   *  - the "?" help: input/correct AND (file-upload OR input) (ActionButtons.vue:25);
   *  - Regions link: instances/summary;
   *  - Save: save-portfolio for a new one, update-portfolio for one that is
   *    already on the server (FooterActions.vue:164-177);
   *  - Delete Portfolio: delete-portfolio; Instance Advice: cost-advice;
   *  - CCA's Cloud Usage Reports link: cloud-accounts (ManageAccount.vue:51).
   */
  const allowed = useFeatureAccess();
  const canUpload = allowed(FEATURES.FILE_UPLOAD);
  const canInput = allowed(FEATURES.INPUT_VALIDATE);
  // Billing (CUR) mode has no Add instance form (legacy ManageAccount.vue:71
  // shows the CCA form only for fileType 'instancesku') — so neither its Find
  // and Replace nor its help.
  const canAddInstance = canInput && !billing;
  const canDetails = canUpload || canInput;
  const canHelp = canDetails && allowed(FEATURES.INPUT_CORRECT);
  const onServer = Boolean(openedPortfolio?.id ?? uploadedPortfolioId.current);
  const canSave = allowed(onServer ? FEATURES.UPDATE_PORTFOLIO : FEATURES.SAVE_PORTFOLIO);
  const canDelete = allowed(FEATURES.DELETE_PORTFOLIO);
  const canAdvice = allowed(FEATURES.COST_ADVICE);
  const canSeeRegions = allowed(FEATURES.REGIONS_LIST);
  /*
   * CCA's "Cloud Usage Reports" link, on legacy's conditions: an Instance SKU
   * portfolio (not Billing), not General IT Average, not OCI, and the
   * cloud-accounts switch. It opens the Cloud Usage Reports page — after
   * "discard changes?" when something here is unsaved, as legacy's
   * cloudUsageReportsRedirect (ManageAccount.vue:558-574). Legacy's prompt
   * only discarded and the link had to be clicked again; Yes goes on here.
   */
  const showCloudUsageReports =
    parts.cloudUsageReports &&
    showsCloudUsageReports({ billing, model: selectedModel, provider }) &&
    allowed(FEATURES.CLOUD_ACCOUNTS);
  const onCloudUsageReports = () => {
    const next = { kind: 'cloud-usage' };
    if (unsaved) setDiscardDialog({ next, busy: false });
    else carryOut(next);
  };
  const tableShown =
    instances.length > 0 || validating || loadingPortfolio || Boolean(openedPortfolio);

  /*
   * The instance table — the product's (PRODUCT_PARTS). EIA puts it in the
   * Instance Stats tab; CCA shows it as it is.
   */
  const instanceTable = (
    <InstanceTableSection
      table={parts.InstanceTable}
      rows={instances}
      provider={provider}
      loading={validating || loadingPortfolio}
      onDelete={onDeleteInstances}
      onDeleteErrors={onDeleteErrorRows}
      actionsDisabled={busy || cellEditing}
      // No onEditCell = cells are read-only (legacy
      // checkPermissionAndEdit, InstancesListTable.vue:920-928).
      onEditCell={canInput ? onEditCell : undefined}
      onEditingChange={setCellEditing}
      emptyMessage={openedPortfolio ? 'No data available' : undefined}
      showRegionsLink={canSeeRegions}
    />
  );

  // Goes in the layout's actionBar slot, not in the content column, so the bar
  // spans the rail as well — the row limit follows the Benchmark Model (3K for
  // General IT Average, 1M otherwise), exactly as legacy FooterActions does.
  const actionBar = (
    <Note
      message={`Please upload a file with maximum of ${getUploadRowLimit(selectedModel)} records`}
    >
      {/* secondary-solid, not secondary: on the Note bar a transparent button
          takes the bar's colour and stops reading as a button.
          Cancel: only with something unsaved to discard (legacy :20). */}
      <Button
        variant="secondary-solid"
        startIcon={<ClearIcon />}
        disabled={!unsaved || busy}
        onClick={() => setDiscardDialog({ next: { kind: 'cancel' }, busy: false })}
      >
        Cancel
      </Button>
      {/* Only while a saved portfolio is open — there is nothing on the server
          to delete before that (legacy :24-30). */}
      {openedPortfolio && canDelete ? (
        <Button
          variant="danger"
          startIcon={<DeleteIcon />}
          disabled={busy || deleteDialog !== null}
          onClick={() => setDeleteDialog('open')}
        >
          Delete Portfolio
        </Button>
      ) : null}
      {canSave ? (
        <Button
          variant="primary"
          startIcon={<SaveIcon />}
          loading={saving}
          disabled={Boolean(saveBlockedBy) || busy}
          title={saveBlockedBy ?? undefined}
          onClick={onSave}
        >
          Save
        </Button>
      ) : null}
      {canAdvice ? (
        <Button
          variant="secondary-solid"
          startIcon={<ChipIcon />}
          disabled={Boolean(adviceBlockedBy) || busy}
          title={adviceBlockedBy ?? undefined}
          onClick={onAdvice}
        >
          <VariantGate only="eia" fallback="Cost Advice">
            Instance Advice
          </VariantGate>
        </Button>
      ) : null}
    </Note>
  );

  return (
    <SidebarLayout
      sidebar={<SidebarPanel />}
      // The 🔑 view has its own buttons; Save / Cancel are for the cards.
      actionBar={cloudView ? undefined : actionBar}
      open={sidebarOpen}
      onOpenChange={(next) => dispatch(setSidebarOpen(next))}
    >
      {cloudView ? (
        <CloudCredentialView
          portfolio={openedPortfolio}
          cloud={provider}
          canDelete={canDelete}
          canAdvice={canAdvice}
          canSeeRegions={canSeeRegions}
          onClose={() => dispatch(setOpenPortfolio(null))}
          onDeleted={() => {
            dispatch(setOpenPortfolio(null));
            resetPage();
          }}
        />
      ) : (
        <div className={styles.stack}>
          <h1 className="sr-only">Manage Portfolio</h1>
          {/* An opened portfolio still loading: the whole page as a skeleton
              (as Instance Advice), not the previous cards with only the table
              shimmering — shaped like this product's page and mode. */}
          {loadingPortfolio ? (
            <ManagePortfolioSkeleton tabs={parts.instanceTabs} addInstance={canAddInstance} />
          ) : (
            <>
              {canDetails ? (
                <PortfolioDetails
                  // A reset (Cancel -> Yes, the ✕, a delete) or a load starts the
                  // card afresh: its "name touched" flag and file error were kept,
                  // so a blank page showed "Portfolio name is required" at once.
                  key={formVersion}
                  name={portfolioName}
                  onNameChange={setPortfolioName}
                  // A saved portfolio keeps its name (legacy ManageAccount.vue:11,
                  // `!!selectedPortfolio?._id`): the name is how it is listed and
                  // looked up, so it is never renamed from here.
                  nameReadOnly={Boolean(openedPortfolio)}
                  contractDiscount={contractDiscount}
                  onContractDiscountChange={setContractDiscount}
                  provider={provider}
                  // The upload into an OPENED portfolio (legacy portfolio_id):
                  // shown only while it has no instances. A new portfolio uploads
                  // from the "No instances added yet" card below instead.
                  onUpload={
                    canUpload && openedPortfolio && instances.length === 0
                      ? onPickInstancesFile
                      : undefined
                  }
                  uploadDisabled={busy}
                  showTemplates={canUpload}
                  // CCA: its templates ("Templates" = the CUR reports in Billing).
                  {...parts.detailsProps({ provider: requestProvider, billing })}
                  extra={
                    showCloudUsageReports ? (
                      <CloudUsageReportsLink onClick={onCloudUsageReports} disabled={busy} />
                    ) : null
                  }
                />
              ) : null}
              {canAddInstance ? (
                <AddInstanceSection
                  key={`${service}|${provider}|${formVersion}`}
                  card={parts.AddInstance}
                  provider={provider}
                  onAdd={onAddInstance}
                  // Locked while the page is busy (saving, uploading, loading, a
                  // cell being re-checked…), as legacy's (+) is during its
                  // loader — so a row cannot be added to a page that is changing.
                  disabled={busy}
                  onHelp={canHelp ? () => setHelpOpen(true) : undefined}
                  // Same permission as the help (legacy shouldHideFindandReplace).
                  onFindReplace={canHelp ? () => setFindReplaceOpen(true) : undefined}
                  findReplaceBlockedBy={findReplaceBlockedBy}
                />
              ) : null}
              {/* The table once there is something in it — or while the first
                  row is being checked, when it shows its skeleton (legacy shows
                  the table's skeleton loader during the check too) — or
                  whenever a SAVED portfolio is open: one with no instances
                  shows its empty table ("No data available", legacy's words),
                  not the new-portfolio upload card, which read as if nothing
                  had been opened. Otherwise (a new portfolio) the empty state
                  with the bulk-upload route. */}
              {!tableShown ? (
                <InstanceUpload
                  onUpload={canUpload ? onPickInstancesFile : undefined}
                  canAdd={canAddInstance}
                  // EIA: the card also takes the Self Perf file, dropped together.
                  selfPerf={parts.selfPerf}
                />
              ) : parts.instanceTabs ? (
                // EIA: Instance Stats | Self Perf Assessment — only once there
                // are instances, as legacy (Instances.vue: v-if instances.length > 0).
                <InstanceTabs
                  instanceStats={instanceTable}
                  selfPerf={
                    selfPerfRows.length > 0 ? <SelfPerfTable rows={selfPerfRows} /> : undefined
                  }
                  // The safety margin belongs to the instance stats — legacy
                  // shows it on that tab only.
                  aside={(tab) =>
                    tab === INSTANCE_TABS.STATS ? (
                      <SafetyMarginSlider value={headroom} onChange={setHeadroom} />
                    ) : null
                  }
                />
              ) : (
                // CCA: the table on its own — no tabs, slider or Self Perf.
                instanceTable
              )}
            </>
          )}

          {uploadDialog ? (
            <UploadDialog
              initialInstancesFile={uploadDialog.file}
              initialSelfPerfFile={uploadDialog.selfPerfFile}
              nameError={validatePortfolioName(portfolioName)}
              stage={uploadStage}
              uploading={uploading}
              // CCA uploads the instances file only (no Self Perf slot).
              selfPerf={parts.selfPerf}
              onUpload={onUpload}
              onClose={() => setUploadDialog(null)}
            />
          ) : null}

          {discardDialog ? (
            // Legacy's wording and No / Yes (FooterActions.vue :66-100) — one
            // dialog for Cancel and for leaving unsaved work via the sidebar.
            <ConfirmDialog
              title="Unsaved Changes"
              message="You have unsaved changes. Are you sure you want to discard changes?"
              cancelLabel="No"
              confirmLabel="Yes"
              danger
              busy={discardDialog.busy}
              onConfirm={onConfirmDiscard}
              onCancel={onCancelDiscard}
            />
          ) : null}

          {helpOpen ? <DataCorrectionHelp onClose={() => setHelpOpen(false)} /> : null}

          {findReplaceOpen ? (
            <FindReplaceSection
              provider={provider}
              rows={instances}
              busy={replacing}
              onReplace={onReplaceAll}
              onClose={() => setFindReplaceOpen(false)}
            />
          ) : null}

          {otherCloud ? (
            // Legacy's wording and buttons (Instances.vue :27-65), and
            // persistent like legacy: only its own buttons close it.
            <ConfirmDialog
              title="Instances from Other Cloud Providers Detected"
              message={`Only the instances from your current cloud portfolio will be processed. Download the Excel file to import the remaining ${otherCloud.rows.length} instance${otherCloud.rows.length === 1 ? '' : 's'} under the correct cloud portfolio.`}
              cancelLabel="Cancel"
              confirmLabel="Download Excel"
              persistent
              busy={otherCloud.busy}
              onConfirm={onDownloadOtherCloud}
              onCancel={() => setOtherCloud(null)}
            />
          ) : null}

          {deleteDialog ? (
            // Legacy's wording and buttons (FooterActions.vue :51-65).
            <ConfirmDialog
              title="Confirm Delete Portfolio"
              message="Are you sure you want to delete this Portfolio?"
              cancelLabel="Cancel"
              confirmLabel="Delete"
              danger
              busy={deleteDialog === 'busy'}
              onConfirm={onConfirmDelete}
              onCancel={() => setDeleteDialog(null)}
            />
          ) : null}
        </div>
      )}
    </SidebarLayout>
  );
}
